import { HttpParams } from '@angular/common/http';
import { ActivatedRoute, Params } from '@angular/router';

import { DataTypeUtils } from '../../../utils/data-type.util';
import { CrudAction } from '../../enums/crud-action.enum';
import { GetParamOptions } from '../interfaces/get-param-options.interfaces';
import { RouteParams } from '../interfaces/route-params.interface';

/**
 * Utilitário para definir e gerar caminhos de rotas na aplicação.
 *
 * @remarks
 * Esta classe fornece duas principais categorias de rotas:
 * - `endpoints`: Definições de caminhos finais das rotas.
 * - `paths`: Caminhos completos que utilizam os endpoints definidos.
 */
export class RouteUtils {
	/**
	 * Definições de endpoints para autenticação.
	 *
	 * @remarks
	 * Inclui os subcaminhos e o caminho principal relacionados à autenticação.
	 * - `auth.path`: Caminho principal para autenticação.
	 * - `auth.child.login`: Caminho para a página de login.
	 * - `auth.child.passwordForgot`: Caminho para a página de recuperação de senha.
	 * - `auth.child.passwordReset`: Caminho para a página de redefinição de senha.
	 */
	static endpoints = {
		auth: {
			child: {
				login: 'login',
				passwordForgot: 'esqueci-a-senha',
				passwordReset: 'redefinir-a-senha',
			},
			path: 'autenticacao',
		},
		beneficiaries: {
			child: {
				history: 'historico',
			},
			path: 'beneficiarios',
		},
		bulkActions: {
			path: 'movimentacoes-em-massa',
		},
		financial: {
			child: {
				management: {
					child: {
						newReport: 'nova-publicacao',
					},
					path: 'gestao',
				},
				reports: 'relatorios',
			},
			path: 'financeiro',
		},
		home: {
			path: 'inicio',
		},
		metrics: {
			child: {
				movements: 'movimentacoes',
			},
			path: 'metricas',
		},
		organizations: {
			allFilter: {
				path: 'todas',
			},
			path: 'organizacoes',
		},
	};
	static params: RouteParams = {
		organizationId: {
			name: 'id da organização',
			value: 'organization_id',
		},
		reportId: {
			name: 'id do relatório',
			value: 'report_id',
		},
	};

	/**
	 * Caminhos completos baseados nos endpoints definidos.
	 *
	 * @remarks
	 * Utiliza os endpoints definidos para gerar caminhos completos para as páginas de autenticação.
	 * - `auth.login`: Caminho completo para a página de login.
	 * - `auth.passwordForgot`: Caminho completo para a página de recuperação de senha.
	 * - `auth.passwordReset`: Caminho completo para a página de redefinição de senha.
	 */
	static paths = {
		auth: {
			login: `/${RouteUtils.endpoints.auth.path}/${RouteUtils.endpoints.auth.child.login}`,
			passwordForgot: `/${RouteUtils.endpoints.auth.path}/${RouteUtils.endpoints.auth.child.passwordForgot}`,
			passwordReset: `/${RouteUtils.endpoints.auth.path}/${RouteUtils.endpoints.auth.child.passwordReset}`,
		},
	};

	/**
	 * Extrai os parâmetros de consulta (query params) de uma URL completa
	 * e os converte em um objeto no formato `{ [key: string]: string }`.
	 *
	 *
	 * @param url - A URL completa contendo os parâmetros de consulta.
	 * @returns Um objeto `Params` com os parâmetros de consulta extraídos.
	 *          Cada chave do objeto representa o nome do parâmetro, e o valor
	 *          associado é o valor do parâmetro como uma string.
	 */
	static getQueryParamsObjectFromString(url: string): Params {
		const queryString = url.split('?')[1] || '';
		const httpParams = new HttpParams({ fromString: queryString });

		// Converter HttpParams para um objeto JavaScript
		const queryParams: { [key: string]: string } = {};
		httpParams.keys().forEach((key) => {
			queryParams[key] = httpParams.get(key) as string;
		});

		return queryParams;
	}

	static getRequiredParam({ param, params }: GetParamOptions): string {
		const value = params.get(param.value);

		if (value === null) {
			throw new Error(`O parâmetro ${param.name} está ausente na rota`);
		}

		return value;
	}

	static getRouteMode(route: ActivatedRoute): CrudAction | undefined {
		let currentRoute: ActivatedRoute | null = route;

		// Verifica nos níveis de rota atuais e pais
		while (currentRoute) {
			const mode = currentRoute.snapshot.data?.mode;

			if (mode) {
				return mode;
			}
			currentRoute = currentRoute.parent;
		}

		return undefined; // Caso não encontre o 'mode' em nenhum nível
	}

	/**
	 * Verifica se o segmento "organizacoes" está presente na URL e se ele é seguido por um UUID válido.
	 *
	 * @param url - A URL a ser verificada.
	 * @returns `true` se "organizacoes" estiver presente e seguido por um UUID válido, caso contrário, `false`.
	 */
	static isOrganizationUuidPresent(url: string): boolean {
		const urlWithNoQueryParams = url.split('?')[0] || ''; // Ignorar os query params
		const pathSegments = urlWithNoQueryParams.split('/').filter(Boolean);
		const organizationIndex = pathSegments.indexOf('organizacoes');

		// Verifica se "organizacoes" está presente e seguida de um UUID válido
		return (
			organizationIndex !== DataTypeUtils.array.notFoundPosition &&
			organizationIndex + 1 < pathSegments.length &&
			DataTypeUtils.string.isUuid(pathSegments[organizationIndex + 1])
		);
	}

	/**
	 * Atualiza o UUID da organização na URL, caso "organizacoes" esteja presente e seguido de um UUID válido.
	 *
	 * @param url - A URL original que contém o UUID da organização.
	 * @param newUuid - O novo UUID que substituirá o existente.
	 * @returns Um array de rotas, com a url atualizada com o novo UUID da organização, se a verificação for bem-sucedida. Caso contrário, retorna a URL original.
	 */
	static updateOrganizationUuidInUrl(
		url: string,
		newUuid: string,
	): { path: string[]; queryParams: Params } {
		const urlWithNoQueryParams = url.split('?')[0] || '';
		const pathSegments = urlWithNoQueryParams.split('/').filter(Boolean);
		const organizationIndex = pathSegments.indexOf('organizacoes');

		// Apenas atualiza se a verificação de UUID for positiva
		if (RouteUtils.isOrganizationUuidPresent(urlWithNoQueryParams)) {
			pathSegments[organizationIndex + 1] = newUuid;
		}

		return {
			queryParams: RouteUtils.getQueryParamsObjectFromString(url),
			path: pathSegments,
		};
	}
}
